import { useParams } from "react-router-dom";
import { toast } from "sonner";
import { useChapterStore } from "@/zapi-frontend/stores/chapter";
import { useChatStore } from "@/zapi-frontend/stores/chatStore";
import { AppButton } from "@libs/zapi-ui/custom";
import { ArrowClockwiseIcon, XIcon } from "@phosphor-icons/react";
import { useEditorStore } from "@/zapi-frontend/stores/editor";
import $api from "@/zapi-frontend/shared/api/axios";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { AxiosError, AxiosResponse } from "axios";
import { useState } from "react";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";

export function RegenerateButton() {
  const { id: projectId } = useParams();
  const { isLabs } = useMeStore();
  const { modelName } = useChatStore();
  const effectiveModel = isLabs ? modelName : null;

  const { setContent } = useEditorStore();
  const { activeChapter } = useChapterStore();
  const { getLoadingState, setLoadingState, resetLoadingState } = useChatStore();

  const [isGenerating, setIsGenerating] = useState(false);
  const [isCancelling, setIsCancelling] = useState(false);

  const chapterId = activeChapter?.id.toString();
  const storeKey = chapterId || "";

  const loadingState = getLoadingState(projectId || "", storeKey);
  const isGeneratingChapter = loadingState.isGeneratingChapter || !!loadingState.pendingGenerationId;
  const currentGenerationId = loadingState.pendingGenerationId;

  const handleGenerateExecutiveSummary = async () => {
    if (!projectId || !activeChapter || !setContent || !chapterId || !storeKey) return;

    setIsGenerating(true);

    const url = `/chat/generate`;
    const memoContent = activeChapter?.activeMemo?.mdContent || "";
    const [response, error] = await tryCatch<AxiosResponse, AxiosError<{ error: string }>>(
      $api.post(url, {
        projectId: Number(projectId),
        chapterId: activeChapter.id,
        content: memoContent,
        messagesHistory: [
          {
            role: "user",
            content: "Regenerate chapter"
          }
        ],
        model: effectiveModel
      })
    );

    setIsGenerating(false);

    if (error) {
      console.error("Error generating Executive Summary:", error);
      const errorMessage = error.response?.data?.error || "Failed to generate Executive Summary";
      toast.error(errorMessage);
      return;
    }

    if (!response?.data?.generationId) {
      toast.error("Failed to start Executive Summary generation");
      return;
    }

    setLoadingState(projectId, storeKey, {
      pendingGenerationId: response.data.generationId,
      isGeneratingChapter: true
    });
  };

  const handleCancelGeneration = async () => {
    if (!currentGenerationId || !projectId || !storeKey) return;

    setIsCancelling(true);
    try {
      await $api.post("/chat/cancel", { generationId: currentGenerationId });
      toast.success("Generation cancelled successfully");
    } catch (error) {
      console.error("Error cancelling generation:", error);
      toast.error("Failed to cancel generation");
    } finally {
      setIsCancelling(false);
      resetLoadingState(projectId, storeKey);
    }
  };

  return (
    <div className="flex gap-2">
      {isGeneratingChapter && !isCancelling && (
        <AppButton
          onClick={handleCancelGeneration}
          size="sm"
          color="red400"
          startAdornment={<XIcon className="h-4 w-4" />}
        >
          Cancel
        </AppButton>
      )}
      <AppButton
        onClick={handleGenerateExecutiveSummary}
        disabled={isGeneratingChapter || isGenerating || isCancelling}
        size="sm"
        startAdornment={<ArrowClockwiseIcon className={`h-4 w-4 ${isGeneratingChapter ? "animate-spin" : ""}`} />}
        isLoading={isGeneratingChapter || isGenerating || isCancelling}
        loadingText={isGenerating ? "Loading" : isCancelling ? "Cancelling" : "Generating"}
      >
        Regenerate
      </AppButton>
    </div>
  );
}
